import * as React from 'react'
import { View } from 'react-native'
import styled from '@theme/styled-components'

import Choice, { Option } from '@components/choice/Choice'
import TextInput from '@components/textInput/TextInput'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'

import { FormikHelpers, useFormik } from 'formik'

import useI18n from '@i18n/useI18n'
import sanitize from '@utils/Sanitizer'

import { isManifestationValidForCQ } from '@screens/controle/form/utils'

interface Values {
  manifestationId?: string
}

const NOW = Date.now()

const Manif = ({ form, onForm }: ControleFormProps) => {
  const i18n = useI18n()

  const init: Values = React.useMemo(() => ({ manifestationId: form.manifestationId }), [form.manifestationId])

  const [search, setSearch] = React.useState('')

  // keep only enabled societes
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const manifs = React.useMemo(
    () =>
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed]
        .filter((m) => isManifestationValidForCQ(m, NOW))
        .filter((m) => sanitize(m.libelle).includes(sanitize(search)))
        .sort((a, b) => a.libelle.localeCompare(b.libelle)),
    [manifestations, search]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const options: Option[] = manifs.map((m) => ({ label: m.libelle, value: m.id }))
  const option = options.find((o) => o.value === values.manifestationId)

  return (
    <MainContainer>
      <SearchContainer>
        <TextInput
          value={search}
          onChange={setSearch}
          placeholder={i18n.t('screens.controles.form.manif.search')}
          left={{ name: 'search' }}
        />
      </SearchContainer>

      <Choice
        data={options}
        onItemSelected={(opt) => setFieldValue('manifestationId', opt.value)}
        value={option}
        inline={false}
      />
    </MainContainer>
  )
}

export default Manif

const MainContainer = styled(View)`
  margin: 0 20px;
  flex: 1;
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 25)}px;
  margin-bottom: 25px;
`
